import React, { useState, useEffect, useRef, useCallback } from "react";
import style from "./AlumniTab.module.css";
import Link from "next/link";

interface MenuItem {
  id: number;
  label: string;
  url: string;
}

interface AlumniTabProps {
  data: MenuItem[];
}

const AlumniTab: React.FC<AlumniTabProps> = ({ data }) => {
  const [activeSection, setActiveSection] = useState<string>("");
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const tabRef = useRef<HTMLDivElement>(null);
  const initialTabOffsetTop = useRef<number>(0);
  const isScrollingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize the tab's initial position
  useEffect(() => {
    if (tabRef.current) {
      initialTabOffsetTop.current = tabRef.current.offsetTop;
    }
  }, []);

  // Check which section is currently in view - memoized to prevent recreating on each render
  const checkActiveSection = useCallback(() => {
    // Skip checking if we're in the middle of a programmatic scroll
    if (isScrollingRef.current) return;

    if (typeof window !== "undefined" && data?.length > 0) {
      const sectionIds = data.map((item) => item.url.replace("#", ""));

      // Get the tab height to use as offset
      const tabHeight = tabRef.current?.offsetHeight || 0;
      // Use the same offset calculation as in handleClick for consistency
      const offset = isSticky ? tabHeight + 120 : tabHeight + 50;

      // Find all sections and their positions
      const sectionPositions = sectionIds
        .map((id) => {
          const section = document.getElementById(id);
          if (!section) return null;

          return {
            id,
            top: section.getBoundingClientRect().top,
            bottom: section.getBoundingClientRect().bottom,
          };
        })
        .filter(Boolean) as Array<{
        id: string;
        top: number;
        bottom: number;
      }>;

      // First, check if any section is near the top of the viewport (accounting for offset)
      const nearTopSection = sectionPositions.find(
        (section) => section.top <= offset + 50 && section.top >= offset - 150
      );

      if (nearTopSection) {
        setActiveSection(`#${nearTopSection.id}`);
        return;
      }

      // If no section is near the top, find the section with the most visibility
      const visibleSections = sectionPositions
        .filter(
          (section) => section.bottom > 0 && section.top < window.innerHeight
        )
        .sort((a, b) => {
          // Calculate how much of each section is visible in the viewport
          const aVisible =
            Math.min(window.innerHeight, a.bottom) - Math.max(0, a.top);
          const bVisible =
            Math.min(window.innerHeight, b.bottom) - Math.max(0, b.top);
          return bVisible - aVisible; // Sort by most visible first
        });

      // Set the most visible section as active
      if (visibleSections.length > 0) {
        setActiveSection(`#${visibleSections[0].id}`);
      }
    }
  }, [data, isSticky]);

  // Handle scroll events for both sticky behavior and active section - memoized
  const handleScroll = useCallback(() => {
    // Check if tab should be sticky
    if (tabRef.current) {
      setIsSticky(window.scrollY > initialTabOffsetTop.current);
    }

    // Use debounced check for active section to reduce performance impact
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      checkActiveSection();
    }, 100); // Small delay to debounce the scroll events
  }, [checkActiveSection]);

  // Set up scroll event listener
  useEffect(() => {
    if (data?.length > 0) {
      window.addEventListener("scroll", handleScroll, { passive: true });
      // Initial check
      handleScroll();

      return () => {
        window.removeEventListener("scroll", handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [data, handleScroll]);

  // Smooth scroll to section without changing URL hash
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault();
      const targetId = href.replace("#", "");
      const element = document.getElementById(targetId);

      if (element) {
        // Calculate offset based on whether the tab is sticky
        const tabHeight = tabRef.current?.offsetHeight || 0;
        const offset = isSticky ? tabHeight + 120 : tabHeight + 50;

        // Immediately set the active section to provide instant feedback
        setActiveSection(href);

        // Mark that we're doing a programmatic scroll
        isScrollingRef.current = true;

        // Scroll to the element
        const targetPosition = element.offsetTop - offset;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });

        // After scrolling is complete, allow scroll detection again
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 800); // Match this to the approximate duration of the smooth scroll
      }
    },
    [isSticky]
  );

  // Early return if no data
  if (!data || data.length === 0) return null;

  return (
    <div
      className={`${style.alumnitabWrapper} ${isSticky ? style.sticky : ""}`}
      ref={tabRef}
    >
      <div className="container">
        <div className={style.alumnitab}>
          <div className={style.alumnitabcontainer} id="alumni-tab">
            {data.map((item) => (
              <Link
                key={item.id}
                href={item.url}
                title={item.label}
                className={activeSection === item.url ? style.active : ""}
                onClick={(e) => handleClick(e, item.url)}
                data-section-id={item.url}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlumniTab;
